import {
  DEFAULT_FILTER_DAYS,
  filtersToApiQuery,
  pick,
  toInputDate,
} from "../dashboard/managementDashboardUtils";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_DIRECTION,
} from "./allRequestPaginationUtils";

export const DRILL_DOWN_FILTER_LABELS = {
  initiallyApproved: "تأیید اولیه‌شده",
  currentlyInExecution: "در حال اجرا",
  executionActive: "در حال اجرا",
  commissioned: "بهره‌برداری‌شده",
  readyForOperations: "آماده ورود به عملیات",
  activeOnly: "فقط فعال",
  needsLoan1: "نیازمند تسهیلات",
  needsLoan0: "بدون نیاز تسهیلات",
  fromDashboard: "از داشبورد",
  searchTerm: "جستجو",
  loanComplete: "وام تکمیل",
  mehrsanComplete: "مهرسان تکمیل",
  blockedByLoan: "مسدود — وام",
  blockedByMehrsan: "مسدود — مهرسان",
  blockedByBoth: "مسدود — هر دو",
  lendingStepCode: "مرحله وام",
  mehrsanStepCode: "مرحله مهرسان",
  overdueAlertCode: "هشدار",
  advancedDespiteGate: "پیشرفت علی‌رغم گیت بسته",
  creditValidated: "اعتبارسنجی‌شده",
  siteVisitAssigned: "بازدید تخصیص‌یافته",
  invalidPreCreditAssignment: "تخصیص پیش از اعتبار",
};

export const FUNNEL_STAGE_LABELS = {
  Registered: "ثبت درخواست",
  InformationCompleted: "تکمیل اطلاعات",
  InitialReview: "بررسی اولیه",
  InitiallyApproved: "تأیید اولیه",
  LoanAndMehrsan: "وام/مهرسان",
  SiteVisit: "بازدید محل",
  CustomerPriceOffer: "پیشنهاد قیمت مشتری",
  CustomerApproved: "تأیید مشتری",
  CustomerContract: "قرارداد مشتری",
  DesignAndProcurement: "طراحی و تأمین",
  Installation: "احداث",
  GridConnection: "اتصال به شبکه",
  Commissioned: "بهره‌برداری",
};

const BOOL_TRUE = new Set(["1", "true", "yes"]);
const BOOL_FALSE = new Set(["0", "false", "no"]);

const readBool = (searchParams, key) => {
  const raw = searchParams.get(key);
  if (raw == null || raw === "") return undefined;
  if (BOOL_TRUE.has(String(raw).trim().toLowerCase())) return true;
  if (BOOL_FALSE.has(String(raw).trim().toLowerCase())) return false;
  return undefined;
};

export const parseAllRequestDrillDownParams = (searchParams) => {
  const read = (key) => searchParams.get(key) || "";

  return {
    dateFrom: read("dateFrom"),
    dateTo: read("dateTo"),
    provinceId: read("provinceId"),
    cityId: read("cityId"),
    projectType: read("projectType"),
    minimumCapacityKw: read("minimumCapacityKw"),
    maximumCapacityKw: read("maximumCapacityKw"),
    needsLoan: read("needsLoan"),
    lendingPartnerCode: read("lendingPartnerCode"),
    contractorInfoId: read("contractorInfoId"),
    requestStatus: read("requestStatus") || read("requestConfirm"),
    registrationChannel: read("registrationChannel"),
    activeOnly: readBool(searchParams, "activeOnly"),
    initiallyApproved: readBool(searchParams, "initiallyApproved"),
    currentlyInExecution:
      readBool(searchParams, "currentlyInExecution") ??
      readBool(searchParams, "executionActive"),
    commissioned: readBool(searchParams, "commissioned"),
    readyForOperations: readBool(searchParams, "readyForOperations"),
    fromDashboard: readBool(searchParams, "fromDashboard"),
    funnelStageCode: read("funnelStageCode"),
    assignmentFilter: read("assignmentFilter") || "all",
    searchTerm: read("searchTerm"),
    partnerReferralLevel1PartnerId:
      read("partnerReferralLevel1PartnerId") || read("partner") || "",
    loanComplete: readBool(searchParams, "loanComplete"),
    mehrsanComplete: readBool(searchParams, "mehrsanComplete"),
    blockedByLoan: readBool(searchParams, "blockedByLoan"),
    blockedByMehrsan: readBool(searchParams, "blockedByMehrsan"),
    blockedByBoth: readBool(searchParams, "blockedByBoth"),
    lendingStepCode: read("lendingStepCode"),
    mehrsanStepCode: read("mehrsanStepCode"),
    overdueAlertCode:
      read("overdueAlertCode") ||
      (readBool(searchParams, "advancedDespiteGate")
        ? "AdvancedDespiteGateFailure"
        : ""),
    creditValidated: readBool(searchParams, "creditValidated"),
    siteVisitAssigned: readBool(searchParams, "siteVisitAssigned"),
    invalidPreCreditAssignment: readBool(searchParams, "invalidPreCreditAssignment"),
    advancedDespiteGate: readBool(searchParams, "advancedDespiteGate"),
    pageNumber: Math.max(1, Number(read("pageNumber") || 1) || 1),
    pageSize: Math.max(1, Number(read("pageSize") || DEFAULT_PAGE_SIZE) || DEFAULT_PAGE_SIZE),
    sortBy: read("sortBy") || DEFAULT_SORT_BY,
    sortDirection: read("sortDirection") || DEFAULT_SORT_DIRECTION,
  };
};

export const drillDownParamsToApiQuery = (params) => {
  const base = filtersToApiQuery({
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    provinceId: params.provinceId,
    cityId: params.cityId,
    projectType: params.projectType,
    minimumCapacityKw: params.minimumCapacityKw,
    maximumCapacityKw: params.maximumCapacityKw,
    needsLoan: params.needsLoan,
    lendingPartnerCode: params.lendingPartnerCode,
    contractorInfoId: params.contractorInfoId,
    requestStatus: params.requestStatus,
    registrationChannel: params.registrationChannel,
  });

  if (params.activeOnly) base.activeOnly = true;
  if (params.initiallyApproved) base.initiallyApproved = true;
  if (params.currentlyInExecution) base.currentlyInExecution = true;
  if (params.commissioned) base.commissioned = true;
  if (params.readyForOperations) base.readyForOperations = true;
  if (params.fromDashboard) base.fromDashboard = true;
  if (params.funnelStageCode) base.funnelStageCode = params.funnelStageCode;
  if (params.searchTerm) base.searchTerm = params.searchTerm;
  if (params.assignmentFilter && params.assignmentFilter !== "all") {
    base.assignmentFilter = params.assignmentFilter;
  }
  if (params.partnerReferralLevel1PartnerId) {
    const partnerId = Number(params.partnerReferralLevel1PartnerId);
    if (Number.isFinite(partnerId) && partnerId > 0) {
      base.partnerReferralLevel1PartnerId = partnerId;
    }
  }
  if (params.loanComplete) base.loanComplete = true;
  if (params.mehrsanComplete) base.mehrsanComplete = true;
  if (params.blockedByLoan) base.blockedByLoan = true;
  if (params.blockedByMehrsan) base.blockedByMehrsan = true;
  if (params.blockedByBoth) base.blockedByBoth = true;
  if (params.lendingStepCode) base.lendingStepCode = params.lendingStepCode;
  if (params.mehrsanStepCode) base.mehrsanStepCode = params.mehrsanStepCode;
  if (params.overdueAlertCode) base.overdueAlertCode = params.overdueAlertCode;
  if (params.creditValidated) base.creditValidated = true;
  if (params.siteVisitAssigned) base.siteVisitAssigned = true;
  if (params.invalidPreCreditAssignment) base.invalidPreCreditAssignment = true;
  if (params.advancedDespiteGate) base.advancedDespiteGate = true;

  base.pageNumber = params.pageNumber || 1;
  base.pageSize = params.pageSize || DEFAULT_PAGE_SIZE;
  base.sortBy = params.sortBy || DEFAULT_SORT_BY;
  base.sortDirection =
    String(params.sortDirection || DEFAULT_SORT_DIRECTION).toLowerCase() === "asc" ? 1 : 2;

  return base;
};

export const drillDownParamsToSearchParams = (params) => {
  const sp = new URLSearchParams();
  const entries = [
    "dateFrom",
    "dateTo",
    "provinceId",
    "cityId",
    "projectType",
    "minimumCapacityKw",
    "maximumCapacityKw",
    "needsLoan",
    "lendingPartnerCode",
    "contractorInfoId",
    "requestStatus",
    "registrationChannel",
    "funnelStageCode",
    "assignmentFilter",
    "searchTerm",
    "lendingStepCode",
    "mehrsanStepCode",
    "overdueAlertCode",
    "pageNumber",
    "pageSize",
    "sortBy",
    "sortDirection",
  ];

  entries.forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      sp.set(key, String(value).trim());
    }
  });

  if (params.activeOnly) sp.set("activeOnly", "1");
  if (params.initiallyApproved) sp.set("initiallyApproved", "1");
  if (params.currentlyInExecution) sp.set("currentlyInExecution", "1");
  if (params.commissioned) sp.set("commissioned", "1");
  if (params.readyForOperations) sp.set("readyForOperations", "1");
  if (params.fromDashboard) sp.set("fromDashboard", "1");
  if (params.loanComplete) sp.set("loanComplete", "1");
  if (params.mehrsanComplete) sp.set("mehrsanComplete", "1");
  if (params.blockedByLoan) sp.set("blockedByLoan", "1");
  if (params.blockedByMehrsan) sp.set("blockedByMehrsan", "1");
  if (params.blockedByBoth) sp.set("blockedByBoth", "1");
  if (params.creditValidated) sp.set("creditValidated", "1");
  if (params.siteVisitAssigned) sp.set("siteVisitAssigned", "1");
  if (params.invalidPreCreditAssignment) sp.set("invalidPreCreditAssignment", "1");
  if (params.advancedDespiteGate) sp.set("advancedDespiteGate", "1");

  if (params.partnerReferralLevel1PartnerId) {
    sp.set("partner", String(params.partnerReferralLevel1PartnerId).trim());
  }

  if (params.pageNumber) sp.set("pageNumber", String(params.pageNumber));
  if (params.pageSize) sp.set("pageSize", String(params.pageSize));
  if (params.sortBy) sp.set("sortBy", String(params.sortBy));
  if (params.sortDirection) sp.set("sortDirection", String(params.sortDirection));

  return sp;
};

const formatDateRangeLabel = (dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return null;
  if (dateFrom && dateTo) return `${dateFrom} تا ${dateTo}`;
  if (dateFrom) return `از ${dateFrom}`;
  return `تا ${dateTo}`;
};

export const buildAppliedFilterChips = (params, options = {}) => {
  const chips = [];
  const {
    provinces = [],
    cities = [],
    projectTypes = [],
    requestStatuses = [],
    lendingPartners = [],
    contractors = [],
    registrationChannels = [],
  } = options;

  const findLabel = (list, value) =>
    list.find((item) => String(pick(item, "value", "Value")) === String(value))?.label
    || list.find((item) => String(pick(item, "value", "Value")) === String(value))?.Label;

  if (params.initiallyApproved) {
    chips.push({ key: "initiallyApproved", label: DRILL_DOWN_FILTER_LABELS.initiallyApproved });
  }
  if (params.currentlyInExecution) {
    chips.push({ key: "currentlyInExecution", label: DRILL_DOWN_FILTER_LABELS.currentlyInExecution });
  }
  if (params.commissioned) {
    chips.push({ key: "commissioned", label: DRILL_DOWN_FILTER_LABELS.commissioned });
  }
  if (params.readyForOperations) {
    chips.push({ key: "readyForOperations", label: DRILL_DOWN_FILTER_LABELS.readyForOperations });
  }
  if (params.activeOnly) {
    chips.push({ key: "activeOnly", label: DRILL_DOWN_FILTER_LABELS.activeOnly });
  }
  if (params.funnelStageCode) {
    chips.push({
      key: "funnelStageCode",
      label: FUNNEL_STAGE_LABELS[params.funnelStageCode] || params.funnelStageCode,
    });
  }

  const provinceLabel = findLabel(provinces, params.provinceId);
  if (provinceLabel) chips.push({ key: "provinceId", label: `استان ${provinceLabel}` });

  const cityLabel = findLabel(cities, params.cityId);
  if (cityLabel) chips.push({ key: "cityId", label: `شهر ${cityLabel}` });

  const rangeLabel = formatDateRangeLabel(params.dateFrom, params.dateTo);
  if (rangeLabel) chips.push({ key: "dateRange", label: rangeLabel, keys: ["dateFrom", "dateTo"] });

  if (params.needsLoan === "1") {
    chips.push({ key: "needsLoan", label: DRILL_DOWN_FILTER_LABELS.needsLoan1 });
  } else if (params.needsLoan === "0") {
    chips.push({ key: "needsLoan", label: DRILL_DOWN_FILTER_LABELS.needsLoan0 });
  }

  const statusLabel = findLabel(requestStatuses, params.requestStatus);
  if (statusLabel) chips.push({ key: "requestStatus", label: `وضعیت: ${statusLabel}` });

  const lendingLabel = findLabel(lendingPartners, params.lendingPartnerCode);
  if (lendingLabel) chips.push({ key: "lendingPartnerCode", label: `تأمین‌کننده: ${lendingLabel}` });

  const contractorLabel = findLabel(contractors, params.contractorInfoId);
  if (contractorLabel) chips.push({ key: "contractorInfoId", label: `پیمانکار: ${contractorLabel}` });

  const channelLabel = findLabel(registrationChannels, params.registrationChannel);
  if (channelLabel) chips.push({ key: "registrationChannel", label: `کانال: ${channelLabel}` });

  const projectTypeLabel = findLabel(projectTypes, params.projectType);
  if (projectTypeLabel) chips.push({ key: "projectType", label: `نوع: ${projectTypeLabel}` });

  if (params.minimumCapacityKw) {
    chips.push({ key: "minimumCapacityKw", label: `حداقل ${params.minimumCapacityKw} kW` });
  }
  if (params.maximumCapacityKw) {
    chips.push({ key: "maximumCapacityKw", label: `حداکثر ${params.maximumCapacityKw} kW` });
  }

  if (params.overdueAlertCode) {
    const alertLabels = {
      AdvancedDespiteGateFailure: DRILL_DOWN_FILTER_LABELS.advancedDespiteGate,
      CreditValidatedNoSiteVisit: "اعتبارسنجی بدون بازدید",
      InitialReviewPending: "بررسی اولیه معطل",
      LendingStepStalled: "توقف مرحله وام",
      MehrsanStepStalled: "توقف مرحله مهرسان",
      ReadyButWorkflowNotAdvanced: "آماده بدون پیشرفت",
      InitiallyApprovedNotSentToLender: "تأیید اولیه بدون لندتک",
      LendingPlatformNoUpdate: "لندتک بدون بروزرسانی",
      MissingNationalIdForLender: "کد ملی ناقص",
      InvalidPreCreditSiteVisitAssignment: "بازدید پیش از اعتبار",
      MissingProjectCapacity: "ظرفیت نامشخص",
    };
    chips.push({
      key: "overdueAlertCode",
      label: `هشدار: ${alertLabels[params.overdueAlertCode] || params.overdueAlertCode}`,
    });
  }
  if (params.advancedDespiteGate && !params.overdueAlertCode) {
    chips.push({
      key: "advancedDespiteGate",
      label: DRILL_DOWN_FILTER_LABELS.advancedDespiteGate,
    });
  }
  if (params.creditValidated) {
    chips.push({ key: "creditValidated", label: DRILL_DOWN_FILTER_LABELS.creditValidated });
  }
  if (params.loanComplete) {
    chips.push({ key: "loanComplete", label: DRILL_DOWN_FILTER_LABELS.loanComplete });
  }
  if (params.mehrsanComplete) {
    chips.push({ key: "mehrsanComplete", label: DRILL_DOWN_FILTER_LABELS.mehrsanComplete });
  }
  if (params.fromDashboard) {
    chips.push({ key: "fromDashboard", label: DRILL_DOWN_FILTER_LABELS.fromDashboard });
  }

  return chips;
};

export const removeDrillDownChip = (params, chip) => {
  const next = { ...params };
  if (chip.keys?.length) {
    chip.keys.forEach((key) => {
      next[key] = "";
    });
    next.pageNumber = 1;
    return next;
  }

  if (["activeOnly", "initiallyApproved", "currentlyInExecution", "commissioned", "readyForOperations", "fromDashboard", "loanComplete", "mehrsanComplete", "blockedByLoan", "blockedByMehrsan", "blockedByBoth", "creditValidated", "siteVisitAssigned", "invalidPreCreditAssignment", "advancedDespiteGate"].includes(chip.key)) {
    next[chip.key] = undefined;
    if (chip.key === "advancedDespiteGate") next.overdueAlertCode = "";
    next.pageNumber = 1;
    return next;
  }

  if (chip.key === "overdueAlertCode") {
    next.overdueAlertCode = "";
    next.advancedDespiteGate = undefined;
    next.pageNumber = 1;
    return next;
  }

  next[chip.key] = "";
  next.pageNumber = 1;
  return next;
};

export const withPageReset = (params) => ({
  ...params,
  pageNumber: 1,
});

export const createDefaultDrillDownParams = () => {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - (DEFAULT_FILTER_DAYS - 1));
  return {
    dateFrom: toInputDate(from),
    dateTo: toInputDate(today),
    provinceId: "",
    cityId: "",
    projectType: "",
    minimumCapacityKw: "",
    maximumCapacityKw: "",
    needsLoan: "",
    lendingPartnerCode: "",
    contractorInfoId: "",
    requestStatus: "",
    registrationChannel: "",
    activeOnly: undefined,
    initiallyApproved: undefined,
    currentlyInExecution: undefined,
    commissioned: undefined,
    readyForOperations: undefined,
    fromDashboard: undefined,
    funnelStageCode: "",
    assignmentFilter: "all",
    searchTerm: "",
    lendingStepCode: "",
    mehrsanStepCode: "",
    overdueAlertCode: "",
    loanComplete: undefined,
    mehrsanComplete: undefined,
    blockedByLoan: undefined,
    blockedByMehrsan: undefined,
    blockedByBoth: undefined,
    creditValidated: undefined,
    siteVisitAssigned: undefined,
    invalidPreCreditAssignment: undefined,
    advancedDespiteGate: undefined,
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: DEFAULT_SORT_BY,
    sortDirection: DEFAULT_SORT_DIRECTION,
  };
};

export const hasDrillDownFilters = (params) =>
  Boolean(
    params?.dateFrom
      || params?.dateTo
      || params?.provinceId
      || params?.cityId
      || params?.projectType
      || params?.minimumCapacityKw
      || params?.maximumCapacityKw
      || params?.needsLoan
      || params?.lendingPartnerCode
      || params?.contractorInfoId
      || params?.requestStatus
      || params?.registrationChannel
      || params?.activeOnly
      || params?.initiallyApproved
      || params?.currentlyInExecution
      || params?.commissioned
      || params?.readyForOperations
      || params?.funnelStageCode
      || params?.searchTerm
      || params?.loanComplete
      || params?.mehrsanComplete
      || params?.blockedByLoan
      || params?.blockedByMehrsan
      || params?.blockedByBoth
      || params?.lendingStepCode
      || params?.mehrsanStepCode
      || params?.overdueAlertCode
      || params?.advancedDespiteGate
      || params?.creditValidated
      || params?.siteVisitAssigned
      || params?.invalidPreCreditAssignment
  );
